import React, { useState, useEffect, useContext } from 'react';
import { FormGroup, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter, Table } from 'reactstrap';
import DatePicker from 'components/DatePicker';
import AlertModal from 'components/AlertModal';
import ConfirmModal from 'components/ConfirmModal';
import { post } from 'services/ApiService';
import { useAlertPlus, useConfirm } from 'hooks';
import config from 'config';
import { getCookie } from 'helpers';
import moment, { Moment } from 'moment';
import { InsurerPractices, Request, Session } from 'types/Request';
import { UserContext } from 'context';
import { useHistory } from 'react-router-dom';
import { checkSession } from 'features/requests/requestsAPI';

export const insurersWithToken = [
  26, // AMFFA
  35, // OSSEG
  36, // Avalian
  47, // CASA (excepto RPG, Vestibular y Drenaje)
  57, // Swiss (excepto internaciones)
  63, // Omint
  66, // Sancor
  74, // Federada (excepto internaciones)
  77, // OSPe
];

export const insurersWithCoinsurance = [
  26, // AMFFA
  35, // OSSEG
  46, // UP
  48, // Galeno
  50, // GalenoDan
  57, // Swiss
  60, // OSAP
  66, // Sancor
  77, // OSPe
];

export const insurersWithPreviousAuthorization = [
  22, // COMEI
  26, // AMFFA
  44, // OSAM
  47, // CASA (RPG, Vestibular y Drenaje)
  55, // Medife
  57, // Swiss
  63, // Omint
  77, // OSPe
];

const insurersAnnulsAllSessions = [
  46, // UP
];

const insurersCheckSessionByDate = [
  71, // Segunda ART
  83, // Swiss ART
];

const insurersWithSessionStatus = [
  60, // OSAP
];

interface SessionsProps {
  requestId: string;
  formData: Request;
  prestaciones: InsurerPractices;
  isLoading: boolean;
  disableSend: boolean;

  setLoading: (value: boolean) => void;
  setSessions: (sessions: Session[]) => void;
  setRecipient: (name: string) => void;
  setFormData?: (formData: Request) => void;
}

export default function Sessions(props: SessionsProps) {
  const {
    setSessions,
    formData,
    setFormData,
    isLoading,
    setLoading,
    disableSend,
    requestId,
    setRecipient,
    prestaciones } = props;

  const user = useContext(UserContext);
  const history = useHistory();

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const [isEnabled, setEnabled] = useState(false);

  const [havePreviousAuthorization, setHavePreviousAuthorization] = useState(false);
  const [requiredPreviousAuthorization, setRequiredPreviousAuthorization] = useState(false);
  const [enablePreviousAuthorization, setEnablePreviousAuthorization] = useState(false);
  const [previousAuthorizationLabel, setPreviousAuthorizationLabel] = useState('');

  const [showToken, setShowToken] = useState(false);
  const [showCoinsurance, setShowCoinsurance] = useState(false);
  const [showSessionStatus, setShowSessionStatus] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [isOriginal, setIsOriginal] = useState(false);
  const [newSessionMinDate, setNewSessionMinDate] = useState<Date>();
  const [newSessionMaxDate, setNewSessionMaxDate] = useState<Date>();
  const [newSession, setNewSession] = useState<{
    date: Date | null;
    token: string | null;
    authorization_id: string | null;
  }>({
    date: null,
    token: null,
    authorization_id: null,
  });
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [excludedDates, setExcludedDates] = useState<Date[]>([]);

  const [annulsAllSessions, setAnnulsAllSessions] = useState(false);
  const [sessionToAnnul, setSessionToAnnul] = useState<number | null>(null);

  useEffect(() => {
    const newExcludedDates: Date[] = [];

    for (const currentSession of formData.sessions) {
      let count = 0;

      // Contamos la cantidad de veces que la fecha de la sesión se cargó
      for (const targetSession of formData.sessions) {
        if (moment(currentSession.date).isSame(targetSession.date, 'day')) {
          count += 1;
        }
      }

      // Obtenemos la cantidad de sesiones que se pueden cargar por día
      let sessionsPerDay = 1;
      if (formData.insurer_id === 36 && (formData.practice_id === 14 || formData.additional_id === 14)) {
        sessionsPerDay = 100;
      } else if (formData.insurer_id === 47 && (formData.practice_id === 14 || formData.additional_id === 14)) {
        sessionsPerDay = 2;
      } else if (formData.insurer_id === 57 && (formData.practice_id === 14 || formData.additional_id === 14)) {
        sessionsPerDay = 2;
      } else if (formData.insurer_id === 55 && (formData.practice_id === 14 || formData.additional_id === 14)) {
        sessionsPerDay = 3;
      } else if (formData.insurer_id === 66 && (formData.practice_id === 14 || formData.additional_id === 14)) {
        sessionsPerDay = 100;
      } else if (formData.insurer_id === 71) {
        sessionsPerDay = formData.max_sessions;
      } else if (formData.insurer_id === 83) {
        sessionsPerDay = formData.max_sessions;
      }

      // Si se cargó menos veces que el máximo no la excluimos
      if (count < sessionsPerDay) {
        continue;
      }

      // Otros casos los agregamos a las fechas a excluir
      newExcludedDates.push(currentSession.date);

      // Excluir toda la semana de la sesión para Swiss con Reeducación Postural
      if (formData.insurer_id === 57 && (formData.practice_id === 9 || formData.additional_id === 9)) {
        const startOfWeek = moment(currentSession.date).startOf('week'); // Inicio de la semana (domingo)

        for (let i = 0; i < 7; i++) {
          const weekDay = moment(startOfWeek).add(i, 'days').toDate();

          // Agregamos el día solo si no está ya en la lista
          if (!newExcludedDates.some(date => moment(date).isSame(weekDay, 'day'))) {
            newExcludedDates.push(weekDay);
          }
        }
      }
    }

    setExcludedDates(newExcludedDates);
  }, [formData.sessions]);

  // Seteamos la etiqueta para autorizaciones previas
  useEffect(() => {
    switch (formData.insurer_id) {
      case 22: // COMEI
        if (quotaExceeded || !!formData.extended_quota) {
          setPreviousAuthorizationLabel('Ingrese Disposición');
        } else {
          setPreviousAuthorizationLabel('Autorización Discapacidad');
        }
        break;
      case 26: // AMFFA
        setPreviousAuthorizationLabel('Autorización Previa');
        break;
      case 44: // OSAM
        if (formData.extended_quota) {
          setPreviousAuthorizationLabel('Número de aprobación');
        }
        break;
      case 55: // Medife
        if (formData.impairment) {
          setPreviousAuthorizationLabel('Autorización Tratamiento de Discapacidad');
        } else if (formData.extended_quota) {
          setPreviousAuthorizationLabel('Número de aprobación');
        } else {
          setPreviousAuthorizationLabel('Autorización Previa');
        }
        break;
      case 57: // Swiss
        if (formData.impairment) {
          setPreviousAuthorizationLabel('Autorización Previa (Para Discapacidad)');
        } else {
          setPreviousAuthorizationLabel('Autorización Previa (Para Especialidades)');
        }
        break;
      case 77: // OSPe
        setPreviousAuthorizationLabel('Validado por OSPE');
        break;
      default:
        setPreviousAuthorizationLabel('Autorización Previa');
    }
  }, [formData.insurer_id]);

  useEffect(() => {
    let withoutPrevAuth = false; // Permite desactivar en casos específicos la autorización previa
    let hasAnnulsAllSessions = false; // Para casos en que anula todas las sesiones en conjunto
    let withToken = insurersWithToken.includes(formData.insurer_id);
    switch (formData.insurer_id) {
      case 22: // COMEI
        if (formData.previously_authorized || quotaExceeded) { // Requere autorización previa o extención de cupo
          withoutPrevAuth = !!formData.authorization_id;
          setPreviousAuthorizationLabel('Ingrese Disposición');
          formData.extended_quota = !!quotaExceeded;
        } else {
          withoutPrevAuth = true;
        }

        withToken = false;
        break;
      case 26: // AMFFA
        // Si es RPG pedimos número autorización si no hay uno guardado
        if (formData.practice_id == 9) {
          withoutPrevAuth = !!formData.authorization_id;
          withToken = false; // RPG usa ConsultarOrdenCompleta, no lleva token
        // Todas las demás prácticas no llevan autorización
        } else {
          withoutPrevAuth = true;
        }
        break;
      case 35: // OSSEG
        if (
          formData.extended_quota ||
          formData.additional_id == 13 // Domicilio
        ) {
          withToken = false;
        }
        break;
      case 36: // Avalian
        // Si es Internación no se utiliza token
        if (formData.practice_id == 14 || formData.additional_id == 14) {
          withToken = false;
        }
        break;
      case 39:
        // Discapacidad requiere número de autorización sólo la primera vez, después lo recupera de la solicitud
        if ( formData.impairment ) {
          withoutPrevAuth = !!formData.authorization_id;
          break;
        }
        break;
      case 47: // CASA
        // RPG (9), Drenaje Linfático (10), Internación (14) y Vestibular (17) no requieren token
        if (formData.practice_id && [9, 10, 14, 17].includes(formData.practice_id)) {
          withToken = false;
          withoutPrevAuth = !!formData.authorization_id;
        }
        break;
      case 44: // OSAM
        // Módulos consultorio, especialidad y REV no requieren autorización previa
        if (formData.extended_quota && formData.authorization_id == '') {
          withoutPrevAuth = false;
          break;
        }

        // Todo lo demás no requiere autorización
        withoutPrevAuth = true;
        break;
      case 46: // UP
        // Si la solicitud tiene número de autorización es porque autorizó sesión de a 5 y anula de a 5
        hasAnnulsAllSessions = !!formData.authorization_id;
        break;
      case 55: // Medife
        // Módulos consultorio, especialidad, drenaje linfático y REV no requieren autorización previa
        if (
          (
            formData.practice_id == 7 ||
            formData.practice_id == 8 ||
            formData.practice_id == 10 ||
            formData.practice_id == 14 ||
            formData.practice_id == 17
          ) &&
          !formData.extended_quota || (formData.extended_quota && formData.authorization_id != '')
        ) {
          withoutPrevAuth = true;
          break;
        }

        // RPG sólo requiere autorización si es domicilio o con plan cobre, niquel, bronce, titanio y plata
        if (formData.practice_id == 9 && // RPG
          (formData.additional_id != 11 && formData.additional_id != 13)&& // Domicilio
          !formData.recipient_plan.toUpperCase().includes('COBRE') && // Plan cobre
          !formData.recipient_plan.toUpperCase().includes('NIQUEL') && // Plan niquel
          !formData.recipient_plan.toUpperCase().includes('BRONCE') && // Plan bronce
          !formData.recipient_plan.toUpperCase().includes('TITANIO') && // Plan titation
          !formData.recipient_plan.toUpperCase().includes('PLATA') // Plan plata
        ) {
          withoutPrevAuth = true;
          break;
        }

        // Discapacidad e internación requieren número de autorización sólo la primera vez, después lo recupera de la solicitud
        if (formData.impairment) {
          withoutPrevAuth = !!formData.authorization_id;
          break;
        }

        // Todo lo demás requiere autorización
        withoutPrevAuth = false;
        break;
      case 57: // Swiss Medical
        // Sólo se solicita el número de autorización si no está ya cargado en la solicitud
        withoutPrevAuth = !!formData.authorization_id;

        // Para discapacidad sólo se solicita el token si no está ya cargado en la solicitud
        if (formData.impairment) {
          withToken = false;

        // Swiss Medical no puede utilizar autorización previa para Módulo Consultorio (id 7) ni Práctica Domicilio (id 13)
        } else if (!!formData.practice_id && [7, 9, 13, 50].includes(formData.practice_id)) {
          withoutPrevAuth = true;

        // Swiss Medical no utiliza autorización previa para nomenclador 25010227
        } else if (!!formData.practice_id && [15, 38, 39, 40].includes(formData.practice_id)) {
          withoutPrevAuth = true;

        // No se tiene que pedir token para prácticas de internación porque es fijo 666
        } else if (!!formData.practice_id && formData.practice_id === 14 || formData.additional_id === 14) {
          withToken = false;
        }
        break;
      case 60: // OSAP
        // OSAP no requiere autorización previa manual (extensión de cupo es automática)
        withoutPrevAuth = true;
        break;
      case 63: // Omint
        if (
          (!!formData.impairment) ||
          (!!formData.practice_id && [9, 11, 17].includes(formData.practice_id)) ||
          (!!formData.additional_id && [11].includes(formData.additional_id))
        ) {
          withToken = false;
          withoutPrevAuth = !!formData.authorization_id;
        } else {
          withToken = true;
          withoutPrevAuth = true;
        }
        break;
      case 66: // Sancor
        if (
          (formData.previously_authorized && formData.authorization_id) ||
          (!!formData.practice_id && formData.practice_id === 14 || formData.additional_id === 14)
        ) {
          withoutPrevAuth = true;
          withToken = false;
        } else {
          withoutPrevAuth = false;
        }
        break;
      case 74: // Federada
        withToken = formData.practice_id != 14 && !formData.extended_quota;
        withoutPrevAuth = false;
        break;
      case 77: // OSPe
        if (quotaExceeded) {
          withoutPrevAuth = false;
        }
        break;
      default:
    }

    setHavePreviousAuthorization(insurersWithPreviousAuthorization.includes(formData.insurer_id) && !withoutPrevAuth);

    setShowCoinsurance(insurersWithCoinsurance.includes(formData.insurer_id));

    setShowSessionStatus(insurersWithSessionStatus.includes(formData.insurer_id));

    setAnnulsAllSessions(insurersAnnulsAllSessions.includes(formData.insurer_id) && hasAnnulsAllSessions);

    setShowToken(withToken);
  }, [
    formData.insurer_id, // Obra Social
    formData.practice_id, // Módulo seleccionada
    formData.additional_id, // Adicional seleccionado
    quotaExceeded, // Flag de cupo excedido
    formData.impairment, // Prestación de discapacidad
    formData.previously_authorized, // Autorización previa
    formData.authorization_id, // Número de autorización solicitud
  ]);

  useEffect(() => {
    /**
     * Prácticas:
     * - 7 -> Modulo Consultorio
     * - 9 -> Reeducación postural
     * - 10 -> Modulo Drenaje Linfático
     * - 38 -> Especialidad Ortopedia y Traumatologia
     * - 39 -> Estimulación temprana y psicomotricidad
     * - 40 -> Especialidad Deportologia
     * - 15 -> Especialidad Pediatria y Neonatologia
     * - 25 -> Especialidad Intensivista
     */

    // Para las solicitudes de COMEI con autorización previa o necesita cupo extendido es obligatorio el código de autorización la primera vez
    if (formData.insurer_id == 22 && // COMEI
        (formData.previously_authorized || quotaExceeded || !!formData.extended_quota) && // Con autorización Previa o Necesita cupo extendido
        !formData.authorization_id?.trim() // Sin número de autorización
    ) {
      setRequiredPreviousAuthorization(true);
      return;

    // AMFFA requiere número de autorización previa para RPG
    } else if (formData.insurer_id == 26 &&
      formData.practice_id && // Módulo elegido
      [9].includes(formData.practice_id) && // RPG
      !formData.authorization_id?.trim() // Sin número de autorización
    ) {
      setRequiredPreviousAuthorization(true);
      return;

    // Si OSAM requiere autorización es obligatoria
    } else if (formData.insurer_id == 44) {
      setRequiredPreviousAuthorization(havePreviousAuthorization);
      return;


    // Algunas prácticas de CASA requieren si o si número de autorización obligatoria
    } else if (formData.insurer_id == 47) {
      if (formData.practice_id && [9, 10, 17].includes(formData.practice_id)) {
        setRequiredPreviousAuthorization(!formData.authorization_id);
      } else {
        setRequiredPreviousAuthorization(false);
      }
      return;

    // Si Medife requiere autorización es obligatoria, exepto para domicilio
    } else if (formData.insurer_id == 55) {
      if (formData.practice_id == 13 || formData.additional_id == 13) {
        setRequiredPreviousAuthorization(false);
      } else {
        setRequiredPreviousAuthorization(havePreviousAuthorization);
      }
      return;

    // Algunas prácticas de Swiss medical requieren si o si número de autorización obligatoria
    } else if (formData.insurer_id === 57 && // Swiss Medical
        formData.practice_id && // Módulo elegido
        [10].includes(formData.practice_id) && // Módulo es RPG o DL
        !formData.impairment && // No es discapacidad
        !formData.authorization_id // No esta seteado el número de aprovación
    ) {
      setRequiredPreviousAuthorization(true);
      return;

    // Si es discapacidad pero todavía no tiene seteado el número de autorización
    } else if (formData.insurer_id === 57 && // Swiss Medical
        formData.impairment && // Es discapacidad
        !formData.authorization_id?.trim() // Sin número de autorización
    ) {
      setRequiredPreviousAuthorization(true);
      return;

    // Omint necesita número de autorización para algunas prácticas
    } else if (
      formData.insurer_id === 63 &&
      (
        (!!formData.practice_id && [9, 11, 17].includes(formData.practice_id)) ||
        (!!formData.additional_id && [11].includes(formData.additional_id)) ||
        (formData.impairment || formData.previously_authorized)
      ) &&
      !formData.authorization_id?.trim()
    ) {
      setRequiredPreviousAuthorization(true);
      return;

    // Para las especialidades y orientaciones de OSPe es obligatoria la autorización previa
    } else if (formData.insurer_id === 77 && formData.practice_id !== 7) {
      setRequiredPreviousAuthorization(true);
      return;

    // Para el módulo consultorio de OSPe es obligatoria la autorización previa si se excedió el cupo
    } else if (formData.insurer_id === 77 && formData.practice_id === 7 && quotaExceeded) {
      setRequiredPreviousAuthorization(true);
      return;

    // Para las Prácticas Domicilio/Internación o falta de cupo de OSPe es obligatoria la autorización previa
    } else if (formData.insurer_id === 77 &&
      (formData.additional_id && [13, 14].includes(formData.additional_id) || quotaExceeded)) {
      setRequiredPreviousAuthorization(true);
      return;
    }

    setRequiredPreviousAuthorization(false);
  }, [
    formData.insurer_id,
    formData.practice_id,
    quotaExceeded,
    formData.previously_authorized,
    formData.impairment,
    formData.authorization_id,
    havePreviousAuthorization,
  ]);

  useEffect(() => {
    let enabled = !!formData.prescription_date && !!formData.recipient_id;

    if (
      ![22, 26, 39, 47, 55, 57, 63, 71].includes(formData.insurer_id) || // Todas las obras sociales menos COMEI, AMFFA, COESBA, CASA, Medife, Swiss, Omint y La Segunda ART
      (formData.insurer_id == 22 && !formData.impairment) || // Solicitudes de discapacidad de COMEI no llevan práctica
      (formData.insurer_id == 26 && !formData.impairment) || // Solicitudes de discapacidad de AMFFA no llevan práctica
      (formData.insurer_id == 39 && !formData.impairment) || // Solicitudes de discapacidad de COESBA no llevan práctica
      (formData.insurer_id == 47 && !formData.impairment) || // Solicitudes de discapacidad de CASA no llevan práctica
      (formData.insurer_id == 55 && !formData.impairment) || // Solicitudes de discapacidad de Medife no llevan práctica
      (formData.insurer_id == 57 && !formData.impairment) || // Solicitudes de discapacidad de Swiss no llevan práctica
      (formData.insurer_id == 63 && !formData.impairment) || // Solicitudes de discapacidad de Omint no llevan práctica
      (formData.insurer_id == 71 && !formData.impairment) // Solicitudes de discapacidad de La Segunda ART no llevan práctica
    ) {
      enabled = enabled && !!formData.practice_id;
    }

    // OSSEG
    if (formData.insurer_id == 35 && formData.extended_quota) {
      enabled = enabled && !!formData?.authorization_id;
    }

    // Osam requiere diagnósitico
    if (formData.insurer_id == 44) {
      enabled = enabled &&
        !!formData?.matricula_profesional_solicitante?.trim() &&
        !!formData?.nombre_profesional_solicitante?.trim();

      // Osam extended quota requiere fecha de aprobación
      if (formData.extended_quota) {
        enabled = enabled && !!formData.authorization_date;
      }
    }

    // UP requiere consultorio
    if ([46].includes(formData.insurer_id)) {
      enabled = enabled && !!formData.consultorio_id;
    }

    // Galeno y GalenoDan
    if ([48, 50].includes(formData.insurer_id)) {
      enabled = enabled &&
        !!formData?.authorization_id &&
        !!formData?.recipient_name &&
        !!formData?.recipient_plan &&
        !!formData?.recipient_tax &&
        !!formData?.consultorio_id &&
        !!formData?.practice_id &&
        !!formData?.coinsurance_name &&
        !!formData?.max_sessions &&
        formData.max_sessions > formData.sessions.length;
    }

    // AMEBPBA
    if ([49].includes(formData.insurer_id)) {
      enabled = enabled &&
        !!formData?.recipient_doc &&
        !!formData?.recipient_name &&
        !!formData?.practice_id &&
        !!formData?.max_sessions &&
        formData.max_sessions > formData.sessions.length;
    }

    // Medifé
    if ([55].includes(formData.insurer_id) && !!formData.extended_quota) {
      enabled = enabled &&
        !!formData?.authorization_id &&
        !!formData?.authorization_date;
    }

    // OSAP: No permitir cargar sesiones si hay pendientes o rechazadas
    if (formData.insurer_id === 60) {
      const hasPendingOrRejected = formData.sessions.some(s => s.status === 2 || s.status === 3);
      enabled = enabled && !hasPendingOrRejected;
    }

    // La Segunda ART
    if (formData.insurer_id == 71) {
      enabled = enabled &&
        !!formData?.authorization_id &&
        !!formData?.recipient_name &&
        !!formData?.diagnosis &&
        !!formData?.max_sessions &&
        formData.max_sessions > formData.sessions.length;
    }

    // Swiss ART
    if (formData.insurer_id == 83) {
      enabled = enabled &&
        !!formData?.authorization_id &&
        !!formData?.recipient_name &&
        !!formData?.recipient_id &&
        !!formData?.diagnosis &&
        !!formData?.max_sessions &&
        formData.max_sessions > formData.sessions.length;
    }

    // Federada requiere diagnósitico
    if (formData.insurer_id == 74) {
      enabled = enabled && !!formData.diagnosis.trim();

      // Federada extended quota requiere número y fecha de aprobación
      if (formData.extended_quota) {
        enabled = enabled &&
          !!formData?.authorization_id &&
          !!formData.authorization_date;
      }
    }

    setEnabled(enabled);
  }, [
    formData.authorization_id,
    formData.prescription_date,
    formData.recipient_id,
    formData.recipient_name,
    formData.recipient_doc,
    formData.recipient_plan,
    formData.recipient_tax,
    formData.recipient_expiration,
    formData.consultorio_id,
    formData.practice_id,
    formData.diagnosis,
    formData.max_sessions,
    formData.impairment,
    formData.authorization_date,
    formData.matricula_profesional_solicitante,
    formData.nombre_profesional_solicitante,
    formData.sessions.length,
    formData.coinsurance_name,
  ]);

  useEffect(() => {
    // Si se desactivó la autorización previa, borramos el dato ingresado
    if (!enablePreviousAuthorization) {
      setNewSession({
        ...newSession,
        authorization_id: null,
      });
    }
  }, [enablePreviousAuthorization]);

  const handleSessionAuthorization = async () => {
    try {
      setLoading(true);

      const checkSessionResult = await checkSession(formData, moment(newSession.date).format('YYYY-MM-DD'));

      const oldOOSS = formData.insurer_id;

      if (checkSessionResult !== true) {
        alert(checkSessionResult);
        return;
      }

      // Verificar que se haya ingresado el número de autorización en los casos que corresponda
      if (
        (requiredPreviousAuthorization || enablePreviousAuthorization) &&
        (!newSession?.authorization_id || !newSession.authorization_id?.trim())
      ) {
        alert('Ingrese el número de autorización previa en el cuadro de texto correspondiente e intente nuevamente.');
        return;
      }

      // Impide que se cree una sesión con el token vacío
      if (showToken &&
        (newSession.token === null || newSession.token?.trim() === '') &&
        formData.insurer_id !== 57 // Para Swiss no es obligatorio
      ) {
        alert('Debe ingresar un token.');
        return;
      }

      // Impedir el uso del token de pruebas de Swisss por parte de los matriculados
      if (showToken && // Token habilitado
        formData.insurer_id === 57 && // Obra social Swiss
        newSession.token && ['555', '666', '999'].includes(newSession.token) // Tokens bloqueados
      ) {
        alert('El número de token ingresado no es válido.');
        return;
      }

      const response = await post('insurers/authorize', {
        insurer_id: formData.insurer_id,
        request_id: requestId,
        previously_authorized: formData.previously_authorized,

        prescriber_id: user?.getMatricula(),
        recipient_id: formData.recipient_id,
        recipient_name: formData.recipient_name ? formData.recipient_name : undefined,
        recipient_apellido: formData.recipient_apellido ? formData.recipient_apellido : undefined,
        recipient_doc: formData.recipient_doc ? formData.recipient_doc : undefined,
        recipient_plan: formData.recipient_plan ? formData.recipient_plan : undefined,
        recipient_tax: formData.recipient_tax ? formData.recipient_tax : undefined,
        recipient_expiration: formData.recipient_expiration ? formData.recipient_expiration : undefined,

        matricula_profesional_solicitante: formData.matricula_profesional_solicitante,
        nombre_profesional_solicitante: formData.nombre_profesional_solicitante,

        practice_id: formData.practice_id,
        practice_text: prestaciones.modules.find(p => p.id === parseInt(`${ formData.practice_id }`))?.name,

        max_sessions: formData.max_sessions ? formData.max_sessions : undefined,
        practice_authorization: newSession.authorization_id,
        practice_date: newSession.date
          ? moment(newSession.date).format('YYYY-MM-DD')
          : undefined,
        practice_token: newSession.token,

        authorization_id: formData.authorization_id ? formData.authorization_id : undefined,
        authorization_date: formData.authorization_date ? formData.authorization_date : undefined,
        extended_quota: formData.extended_quota ? formData.extended_quota : undefined,

        consultorio_id: formData.consultorio_id ? formData.consultorio_id : undefined,

        coinsurance_name: formData.coinsurance_name ? formData.coinsurance_name : undefined,

        additional_id: formData.additional_id,
        additional_text: formData.additional_id
          ? prestaciones.additionals.find(p => p.id === parseInt(`${ formData.additional_id }`))?.name
          : null,

        diagnosis: formData.diagnosis,

        quirurgical: formData.quirurgical,
        impairment: formData.impairment,

        prescription_date: formData.prescription_date
          ? moment(formData.prescription_date).format('YYYY-MM-DD')
          : undefined,
      });

      // Errores
      if (response.status !== 'ok') {
        if (response.code === 'REQUIRE_AUTHORIZATION') {
          setQuotaExceeded(true);
          formData.extended_quota = true;
          if (formData.insurer_id == 22) {
            alert('Tope superado. \n\rCerrar e ingresar el nro. de disposición emitido por el Comei.');
          } else {
            alert(response.message);
          }
          return;
        }

        if (response.code === 'MATRICULATE_INACTIVE') {
          alert('Su usuario no se encuentra activo para realizar esta operación.');
          return;
        }

        if (response.message) {
          alert(response.message);
          return;
        }

        alert('Hubo un problema al intentar autorizar la práctica. Por favor, intente nuevamente más tarde');
        return;
      }

      if (response.payload?.recipient?.name) {
        setRecipient(response.payload.recipient.name);
      }

      if (
        // En el caso de discapacidad UP y Omint si no está seteado aún guardamos el número de autorización
        (formData.insurer_id === 46 && setFormData && !formData.authorization_id) ||
        (formData.insurer_id === 63 && setFormData && !formData.authorization_id) ||
        // En el caso de discapacidad Medife si no está seteado aún guardamos el número de autorización
        (formData.insurer_id === 55 && setFormData && formData.impairment && !formData.authorization_id) ||
        // Medife en caso de ser un cupo extendido
        (formData.insurer_id === 55 && setFormData && formData.extended_quota) ||
        // Omint
        (
          formData.insurer_id === 63 &&
          setFormData &&
          (
            (!!formData.practice_id && [9, 11, 12, 17].includes(formData.practice_id)) ||
            (!!formData.additional_id && [11, 12].includes(formData.additional_id))
          )
        ) ||
        // OSAM en caso de ser un cupo extendido
        (formData.insurer_id === 44 && setFormData && formData.extended_quota) ||
        // En el caso de discapacidad Swiss si no está seteado aún guardamos el número de autorización
        (formData.insurer_id === 57 && setFormData && formData.impairment && !formData.authorization_id) ||
        // En el caso de autorización previa COMEI si no está seteado aún guardamos el número de autorización
        (formData.insurer_id === 22 && setFormData && !formData.authorization_id) ||
        // En el caso de autorización previa AMFFA si no está seteado aún guardamos el número de autorización
        (formData.insurer_id === 26 &&
          setFormData &&
          formData.practice_id &&
          [9].includes(formData.practice_id) && // RPG
          !formData.authorization_id)
      ) {
        setFormData({
          ...formData,
          sessions: [
            ...formData.sessions,
            {
              date: moment(response.payload.session.date, 'YYYY-MM-DD').toDate(),
              date_parsed: moment(response.payload.session.date, 'YYYY-MM-DD').format('DD/MM/YYYY'),
              authorization_id: response.payload.session.approvalNumber,
              coinsurance: response.payload.session.coinsurance,
              status: response.payload.session.status,
            } as Session,
          ],
          authorization_id: newSession.authorization_id
            ? newSession.authorization_id
            : response.payload.billing_request.numero_aprobacion,
        });

      // Todas las demas obras sociales y otras solicitudes de Swiss
      } else {
        // Agregamos al arreglo de fechas de sesiones la nueva fecha
        setSessions([
          ...formData.sessions,
          {
            date: moment(response.payload.session.date, 'YYYY-MM-DD').toDate(),
            date_parsed: moment(response.payload.session.date, 'YYYY-MM-DD').format('DD/MM/YYYY'),
            authorization_id: response.payload.session.approvalNumber,
            coinsurance: response.payload.session.coinsurance,
            status: response.payload.session.status,
          } as Session,
        ]);
      }

      setOpen(false);

      // OSAP: Mensaje cuando se excede el tope de sesiones
      if (formData.insurer_id === 60 && response.payload.session.status === 2) {
        alert(
          'Se alcanzó el tope de sesiones autorizadas. La sesión quedó en estado Pendiente.\n\n' +
          'El afiliado debe tramitar la extensión de cupo directamente con OSAP. ' +
          'Una vez aprobada, la sesión se autorizará automáticamente.');

      // Galeno y GalenoDan cambian de obra social según el consultorio
      } else if (
        [48, 50].includes(response.payload.billing_request.ooss_id) &&
        response.payload.billing_request.ooss_id !== oldOOSS
      ) {
        if (response.payload.session.coinsurance > 0) {
          alert(`Coseguro a cargo del afiliado por sesión: $ ${response.payload.session.coinsurance}`,
            () => {
              alert(
                'El consultorio seleccionado pertenece al convenio con otra obra social. ' +
                'Se redirigirá a la misma.',
                () => {
                  if (response.payload.billing_request.ooss_id === 48) {
                    history.push(`/solicitudes/${formData.id}/edit-galeno`);
                  } else {
                    history.push(`/solicitudes/${formData.id}/edit-galenodan`);
                  }
                },
              );
            },
          );
        } else {
          alert(
            'El consultorio seleccionado pertenece al convenio con otra obra social. ' +
            'Se redirigirá a la misma.',
            () => {
              if (response.payload.billing_request.ooss_id === 48) {
                history.push(`/solicitudes/${formData.id}/edit-galeno`);
              } else {
                history.push(`/solicitudes/${formData.id}/edit-galenodan`);
              }
            },
          );
        }
      } else if (response.payload.session.coinsurance > 0) {
        alert(`Coseguro a cargo del afiliado por sesión: $ ${response.payload.session.coinsurance}`);
      }
    } catch (err) {
      console.error(err);
      alert('Hubo un problema al intentar autorizar la práctica. Por favor, intente nuevamente más tarde');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionAnnulment = async (target: number) => {
    try {
      setLoading(true);

      const response = await post('insurers/annul', {
        request_id: requestId,
        insurer_id: formData.insurer_id,
        recipient_id: formData.recipient_id,
        authorization_id: formData.sessions[target].authorization_id,
        session_date: formData.sessions[target].date_parsed,
      });

      // Errores
      if (response.status !== 'ok') {
        if (response.message) {
          alert(response.message);
        } else {
          alert('Hubo un problema al intentar anular la práctica. Por favor, intente nuevamente más tarde');
        }

        setLoading(false);
        return;
      }

      if (annulsAllSessions) {
        setSessions([]);
      } else {
        const sessions = formData.sessions.filter((value, idx) => idx !== target);
        setSessions(sessions);
      }

      // Para el caso de UP, si está seteado, sacamos el número de autorización
      if (formData.insurer_id === 46 && setFormData && formData.authorization_id) {
        setFormData({
          ...formData,
          sessions: [],
          authorization_id: '',
        });
      }
    } catch (err) {
      console.error(err);
      alert('Hubo un problema al intentar validar el número de beneficiario. Por favor, intente nuevamente más tarde');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionAnnulmentConfirmation = (target: number) => {
    const session = formData.sessions[target];
    const sessionDate = session.date_parsed || moment(session.date).format('DD/MM/YYYY');

    setSessionToAnnul(target);
    setConfirm(
      true,
      `¿Está seguro de que desea anular la sesión del día ${ sessionDate }?`,
      'session-annulment',
    );
  };

  const handleConfirmation = (action: string) => {
    if (action === 'session-annulment' && sessionToAnnul !== null) {
      handleSessionAnnulment(sessionToAnnul);
    }

    setSessionToAnnul(null);
    setConfirm(false);
  };

  const showNewSessionModal = async () => {
    if (!isOriginal && !insurersCheckSessionByDate.includes(formData.insurer_id)) {
      const response = await fetch(`${ config.baseUrl }/requests/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getCookie('accessToken'),
        },
        body: JSON.stringify({
          request_id: requestId,
          insurer_id: formData.insurer_id,
          recipient_id: formData.recipient_id,
          practice_id: formData.practice_id,
          additional_id: formData.additional_id && formData.additional_id != -1 ? formData.additional_id : undefined,
          prescription_date: formData.prescription_date
            ? moment(formData.prescription_date).format('YYYY-MM-DD')
            : undefined,
        }),
      });

      const output = await response.json();

      if (output.status === 'ok' && output.payload) {
        if (output.payload.estado_id === 1 || output.payload.estado_id === 4) {
          alert('Ya existe una solicitud en curso para este afiliado con esta fecha de prescripción. ' +
            'Si desea agregar más sesiones al tratamiento debe hacerlo en la misma.' +
            `Ahora será redirigido a la solicitud #${ output.payload.id }`, () => {
            if (formData.insurer_id === 22) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-comei`);
            } else if (formData.insurer_id === 26) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-amffa`);
            } else if (formData.insurer_id === 35) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-osseg`);
            } else if (formData.insurer_id === 46) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-up`);
            } else if (formData.insurer_id === 48) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-galeno`);
            } else if (formData.insurer_id === 49) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-amebpba`);
            } else if (formData.insurer_id === 50) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-galenodan`);
            } else if (formData.insurer_id === 66) {
              if (formData.previously_authorized) {
                history.replace(`/solicitudes/${ output.payload.id }/edit-sancor-4`);
              } else {
                history.replace(`/solicitudes/${ output.payload.id }/edit-sancor`);
              }
            } else if (formData.insurer_id === 55) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-medife`);
            } else if (formData.insurer_id === 60) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-osap`);
            } else if (formData.insurer_id === 57) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-swiss`);
            } else if (formData.insurer_id === 63) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-omint`);
            } else if (formData.insurer_id === 74) {
              if (formData.previously_authorized) {
                history.replace(`/solicitudes/${ output.payload.id }/edit-federada-p`);
              } else {
                history.replace(`/solicitudes/${ output.payload.id }/edit-federada`);
              }
            } else if (formData.insurer_id === 80) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-amsterdam`);
            } else if (formData.insurer_id === 47) {
              history.replace(`/solicitudes/${ output.payload.id }/edit-casa`);
            }
            history.replace(`/solicitudes/${ output.payload.id }/edit-sessions`);
          });

          return;
        }
        if (output.payload.estado_id === 2) {
          alert(`Verifique la Solicitud ${ output.payload.id } enviada al auditor. Por favor, comunicarse con la DR ` +
          'para solicitar su retorno a su bandeja de tratamientos para dar continuidad al mismo');
          return;
        }
      }

      setIsOriginal(true);
    }

    // Calculamos la fecha mínima para las sesiones
    let newMinDate: Moment;
    switch (formData.insurer_id) {
      case 22: // COMEI
        newMinDate = moment();
        break;
      case 26: // AMFFA
        newMinDate = moment(); // Solo hoy - requerido por uso de token
        break;
      case 39: // COESBA
      case 57: // Swiss
        if (formData.impairment) {
          newMinDate = moment().subtract(364, 'days');
        } else {
          newMinDate = moment().subtract(6, 'days');
        }
        break;
      case 35: // OSSEG
      case 36: // Avalian
        newMinDate = moment();
        break;
      case 48: // Galeno
      case 49: // AMEBPBA
      case 50: // GalenoDan
        newMinDate = moment().subtract(5, 'days');
        break;
      case 55: // Medife
        if (formData.impairment) {
          newMinDate = moment().subtract(364, 'days');
        } else if (formData.extended_quota) {
          newMinDate = moment().startOf('month');
        } else {
          newMinDate = moment().subtract(6, 'days');
        }
        break;
      case 60: // OSAP
        newMinDate = moment().subtract(5, 'days');
        break;
      case 63: // Omint
        newMinDate = moment();
        break;
      case 66: // Sancor
        newMinDate = moment().subtract(6, 'days');
        break;
      case 46: // UP
        // Solo hoy
        newMinDate = moment();
        break;
      case 71: // La Segunda ART
      case 83: // Swiss ART
        newMinDate = moment().subtract(5, 'days');
        break;
      case 77: // OSPe
        // Solo hoy
        newMinDate = moment();
        break;
      case 74: // Federada
        // Fecha mínima: Federada 7 días excepto internación
        if (formData.practice_id === 14) {
          newMinDate = moment().subtract(364, 'days');
        } else {
          newMinDate = moment().subtract(6, 'days');

          if (formData.extended_quota && newMinDate.isBefore(formData.authorization_date, 'day')) {
            newMinDate = moment(formData.authorization_date, 'YYYYMMDD');
          }
        }
        break;
      case 80: // Amsterdam
        // Solo hoy
        newMinDate = moment();
        break;
      case 47: // CASA
        // Solo hoy
        newMinDate = moment();
        break;
      default:
        newMinDate = formData?.prescription_date
          ? moment(formData.prescription_date)
          : moment();
    }

    // Si la fecha de prescripción es mayor a la fecha mínima, ajustamos la fecha mínima
    if (formData?.prescription_date && newMinDate.isBefore(formData.prescription_date, 'day')) {
      newMinDate = moment(formData.prescription_date);
    }

    // Calculamos la fecha máxima para las sesiones
    let newMaxDate = moment();

    // Las sesiones de una prestación por discapacidad de Swiss tienen que estar dentro del mismo año calendario
    if (formData.insurer_id == 57 && formData.impairment && formData.sessions.length > 0) {
      const firstSessionDate = formData.sessions
        .map(({ date }) => date)
        .sort((a, b) => a.valueOf() - b.valueOf())[0];

      // Si el nuevo máximo se va del año de la primera sesión lo ajustamos
      if (newMaxDate.isAfter(firstSessionDate, 'year')) {
        newMaxDate = moment(firstSessionDate).endOf('year');
      }
    }


    // Si el mes es diferente ajustamos la fecha mínima al primer día del mes en curso
    // Excepto en el caso de discapacidad
    if (!formData.impairment) {
      if (!newMinDate.isSame(moment(), 'month')) {
        newMinDate = moment().startOf('month');
      }

      if (!newMaxDate.isSame(moment(), 'month')) {
        newMaxDate = moment().endOf('month');
      }
    }

    // Si la fecha es anterior a 24/06/2024 se pone esa como mínima (fecha de puesta en producción)
    if (formData.insurer_id == 44) {
      const deployDate = moment('20240624', 'YYYYMMDD');
      newMinDate = newMinDate.isBefore(deployDate, 'day') ? deployDate : newMinDate;

      if (formData.extended_quota) {
        newMinDate = !!formData.authorization_date ? moment(formData.authorization_date, 'YYYYMMDD') : newMinDate;
      }
    }

    // Calculamos la fecha sugerida
    let suggestedDate: Moment | null = newMinDate;

    // Si hay fechas de sesión tomamos el día siguiente a la última fecha
    if (formData.sessions.length) {
      const lastSessionDate = moment(formData.sessions
        .map(({ date }) => date)
        .sort((a, b) => {
          return b.valueOf() - a.valueOf();
        })[0]);

      if (lastSessionDate.clone().add(1, 'day').isSameOrBefore(moment(), 'day')) {
        suggestedDate = lastSessionDate.add(1, 'day');
      }
    }

    // Controlamos que la fecha sugerida esté entre los límites
    const invalidMin = suggestedDate.isBefore(newMinDate, 'day');
    const invalidMax = suggestedDate.isAfter(newMaxDate, 'day');
    if (invalidMin && invalidMax) {
      return;
    }

    if (invalidMin) {
      suggestedDate = newMinDate.clone();
    } else if (invalidMax) {
      suggestedDate = newMaxDate.clone();
    }

    let count = 0;
    for (const currentSession of formData.sessions) {
      if (suggestedDate.isSame(currentSession.date, 'day')) {
        count += 1;
      }
    }

    // Cantidad de sesiones por día
    let sessionsPerDay = 1;
    if (formData.insurer_id === 47 && (formData.practice_id === 14 || formData.additional_id === 14)) {
      sessionsPerDay = 2;
    } else if (formData.insurer_id === 57 && (formData.practice_id === 14 || formData.additional_id === 14)) {
      sessionsPerDay = 2;
    } else if (formData.insurer_id === 74 && (formData.practice_id === 14 || formData.additional_id === 14)) {
      sessionsPerDay = 2;
    } else if (formData.insurer_id === 55 && (formData.practice_id === 14 || formData.additional_id === 14)) {
      sessionsPerDay = 3;
    } else if (formData.insurer_id === 66 && (formData.practice_id === 14 || formData.additional_id === 14)) {
      sessionsPerDay = 100;
    } else if (formData.insurer_id === 71) {
      sessionsPerDay = formData.max_sessions;
    }

    // Si ya está esa cantidad de sesiones cargadas no hacemos la sugerencia
    if (count >= sessionsPerDay) {
      suggestedDate = null;
    }

    setNewSessionMinDate(newMinDate.toDate());
    setNewSessionMaxDate(newMaxDate.toDate());

    let newSessionToken: typeof newSession.token = null;

    if (formData.insurer_id === 57 && (formData.practice_id === 14 || formData.additional_id === 14)) {
      newSessionToken = '666';
    }

    setNewSession({
      date: suggestedDate ? suggestedDate.toDate() : null,
      token: newSessionToken,
      authorization_id: null,
    });

    // Mostramos el modal
    setOpen(true);
  };

  return (
    <>
      <Button
        className="d-block"
        color="primary"
        onClick={ () => {
          showNewSessionModal();
        } }
        disabled={ isLoading || !isEnabled }
      >
        Agregar sesión
      </Button>
      <small style={{ color: '#e67f83' }} className={`mt-2 ${ formData?.prescription_date ? 'd-none' : 'd-block' }`}>
        Falta seleccionar la fecha de prescripción
      </small>
      <small style={{ color: '#e67f83' }} className={`mt-2 ${ formData?.recipient_id ? 'd-none' : 'd-block' }`}>
        Falta indicar el número de afiliado
      </small>
      <small className={`mt-2 ${ (formData?.insurer_id == 46 && formData?.authorization_id) ? 'd-block' : 'd-none' }`}>
        El tratamiento será autorizado por un lote de 5 (cinco) sesiones bajo un mismo número de autorización.
      </small>
      <small style={{ color: '#e67f83' }} className={`mt-2 ${
        formData?.insurer_id === 60 && formData?.sessions?.some(s => s.status === 2)
          ? 'd-block' : 'd-none'
      }`}>
        No se pueden cargar más sesiones mientras haya sesiones pendientes de auditoría
      </small>
      <small style={{ color: '#e67f83' }} className={`mt-2 ${
        formData?.insurer_id === 60 && formData?.sessions?.some(s => s.status === 3)
          ? 'd-block' : 'd-none'
      }`}>
        No se pueden cargar más sesiones porque hay una sesión rechazada
      </small>
      <Table className='align-items-center table-flush mb-0' style={{ fontSize: '.8rem' }} responsive={ false }>
        <thead>
          <tr>
            <th>FECHA SESIÓN</th>
            <th>AUTORIZACIÓN</th>
            { showCoinsurance ? <th>COSEGURO</th> : null }
            { showSessionStatus ? <th>ESTADO</th> : null }
            <th>ACCIONES</th>
          </tr>
        </thead>
        <tbody>
          {
            formData && formData.sessions && Array.isArray(formData.sessions)
              ? formData.sessions
                .sort((a, b) => a.date.valueOf() - b.date.valueOf())
                .map(({ date, authorization_id: authorizationId, coinsurance, online, status }, i) => (
                  <tr key={ i }>
                    <td>{ date.toLocaleDateString('es') }</td>
                    <td>{ authorizationId ?? ' - ' }</td>
                    { showCoinsurance ? <td>$ { coinsurance ?? '0.00' }</td> : null }
                    { showSessionStatus ? (
                      <td>
                        { status === 1 && <span className="badge badge-success">Autorizado</span> }
                        { status === 2 && <span className="badge badge-warning">Pendiente</span> }
                        { status === 3 && <span className="badge badge-danger">Rechazado</span> }
                        { !status && ' - ' }
                      </td>
                    ) : null }
                    <td className='text-left table__mobile--body__actions'>
                      {
                      // Sancor devuelve número de autorización 0 en Test
                      // OSAP: No se pueden anular sesiones rechazadas (status=3)
                        (!annulsAllSessions || i == 0) &&
                        status !== 3 &&
                        (
                          authorizationId ||
                          (formData.insurer_id === 66 && authorizationId !== '') ||
                          (formData.insurer_id === 74 && !online)
                        )
                          ? <Button
                              color="warning"
                              size="sm"
                              onClick={ () => handleSessionAnnulmentConfirmation(i) }
                              disabled={ isLoading }
                            >{ !annulsAllSessions ? 'Anular' : 'Anular Todas' }</Button>
                          : null
                      }
                    </td>
                  </tr>
                ))
              : null
          }
        </tbody>
      </Table>
      <Modal isOpen={ isOpen } size="md" centered>
        <ModalHeader>Agregar fecha de sesión</ModalHeader>
        <ModalBody>
          <FormGroup className='card form-group-dates'>
            <Label>Fecha</Label>
            <DatePicker
              id="session_date"
              name="session_date"
              onChange={ date => {
                if (!date) {
                  date = new Date();
                }

                setNewSession({ ...newSession, date: new Date(date.setHours(0, 0, 0, 0)) });
              }}
              dateFormat="dd/MM/yyyy"
              locale="es"
              placeholdertext="Fecha de la sesión"
              minDate={ newSessionMinDate }
              maxDate={ newSessionMaxDate }
              excludeDates={ excludedDates }
              selected={ newSession.date }
              className="form-control"
              autoComplete="off"
              disabled={ isLoading }
            />
          </FormGroup>
          {
            havePreviousAuthorization
              ? <FormGroup>
                  <div className="custom-control custom-checkbox">
                    <Input
                      id="session_have_authorization"
                      name="session_have_authorization"
                      type="checkbox"
                      checked={ requiredPreviousAuthorization || enablePreviousAuthorization }
                      onChange={ ({ target }) =>
                        setEnablePreviousAuthorization(target.checked && havePreviousAuthorization)
                      }
                      disabled={ isLoading }
                      style={{ marginTop: '.15rem' }}
                    />
                    <Label for="session_have_authorization" className="form-check-label">
                      { previousAuthorizationLabel }
                    </Label>
                  </div>
                  <Input
                    id="session_authorization_id"
                    name="session_authorization_id"
                    value={ newSession.authorization_id ?? '' }
                    onChange={ ({ target }) => setNewSession({ ...newSession, authorization_id: target.value }) }
                    disabled={ isLoading || (!requiredPreviousAuthorization && !enablePreviousAuthorization)}
                    required
                  />
                </FormGroup>
              : null
          }
          {
            showToken
              ? <FormGroup>
                  <Label for="session_token">Token de Seguridad</Label>
                  <Input
                    id="session_token"
                    name="session_token"
                    value={ newSession.token ? newSession.token : '' }
                    onChange={ ({ target }) => setNewSession({ ...newSession, token: target.value.trim() }) }
                    disabled={ isLoading }
                    required
                  />
                </FormGroup>
              : null
          }
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={ handleSessionAuthorization }
            disabled={ isLoading || disableSend || !newSession.date }
          >
            { requiredPreviousAuthorization || enablePreviousAuthorization ? 'Validar' : 'Autorizar' }
          </Button>
          <Button
            color="primary"
            onClick={ () => setOpen(false) }
            disabled={ isLoading }
          >
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
      <AlertModal isOpen={ showAlert } message={ message } onClose={ alertCallback } />
      <ConfirmModal
        isOpen={ confirm }
        title={ confirmTitle }
        action={ confirmAction }
        onClose={ () => {
          setSessionToAnnul(null);
          setConfirm(false);
        } }
        onConfirm={ handleConfirmation }
      />
    </>
  );
}
